import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, Mail, Lock, LogIn } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import "./Login.css";

const Login = () => {
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "", general: "" });
  const [loading, setLoading] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setErrors({ email: "", password: "", general: "" });

    // Manual validation for empty fields
    if (!credentials.email || !credentials.password) {
      setErrors({
        email: "",
        password: "",
        general: "Email or password is incorrect. Please try again."
      });
      setLoading(false);
      return;
    }

    const result = await login(credentials);

    if (result.success) {
      navigate("/");
    } else {
      const errorMsg = result.error;

      // Rule: If both are wrong (handled by general case) or any generic failure
      if (errorMsg === "Invalid email") {
        setErrors({ email: "email incorrect", password: "", general: "" });
      } else if (errorMsg === "Invalid password") {
        setErrors({ email: "", password: "password incorrect", general: "" });
      } else {
        // This covers "Email or password is incorrect. Please try again."
        // for cases where BOTH are wrong or any other auth failure.
        setErrors({
          email: "",
          password: "",
          general: "Email or password is incorrect. Please try again."
        });
      }
    }

    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: value,
    });
    // Clear error for the field being edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: "" }));
    }
  };

  return (
    <div className="login-page-wrapper">
      <div className="login-card-container">
        <div className="glass-card">
          <div className="login-header">
            <div className="login-logo-container">
              <img
                src="/ca.png"
                alt="CA Logo"
                className="login-logo"
              />
            </div>
            <h2 className="login-title">CA Super Admin</h2>
            <p className="login-subtitle">Document Management System</p>
          </div>

          <div className="login-form-body">
            <form onSubmit={handleSubmit} noValidate>
              <div className="input-container">
                <label htmlFor="email" className="input-label">
                  Email Address
                </label>
                <div className={`input-field-wrapper ${errors.email ? 'input-error' : ''}`}>
                  <Mail className="input-icon" size={20} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={credentials.email}
                    onChange={handleChange}
                    className="custom-input"
                    placeholder="Enter your email"
                    disabled={loading}
                  />
                </div>
                {errors.email && (
                  <div className="field-error">
                    <AlertCircle size={14} />
                    <span>{errors.email}</span>
                  </div>
                )}
              </div>

              <div className="input-container">
                <label htmlFor="password" className="input-label">
                  Password
                </label>
                <div className={`input-field-wrapper ${errors.password ? 'input-error' : ''}`}>
                  <Lock className="input-icon" size={20} />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={credentials.password}
                    onChange={handleChange}
                    className="custom-input"
                    placeholder="Enter your password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && (
                  <div className="field-error">
                    <AlertCircle size={14} />
                    <span>{errors.password}</span>
                  </div>
                )}
              </div>

              {errors.general && (
                <div className="error-container">
                  <AlertCircle size={20} />
                  <span>{errors.general}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="login-btn"
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <LogIn size={20} />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <div className="login-footer">
          <p>© 2024 CA Direct • Secure Portal</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
